import React, { useMemo, useState, useRef } from "react";
import PropTypes from "prop-types";
import * as Sentry from "@sentry/react";
import { Document, Page, pdfjs } from "react-pdf";
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.js?url";
import {
  faChevronRight,
  faChevronLeft,
} from "@fortawesome/free-solid-svg-icons";

import LoaderComponent from "../../../loader/Loader.component";
import { getLabelMessage } from "../../../../const/labelService";
import {
  NextPdfPage,
  PreviousPdfPage,
  PdfContainer,
  PdfPageCounterContainer,
  NextPdfButton,
  PreviousPdfButton,
  PageCounter,
  DocumentContainer,
  PdfFallback,
  PdfFallbackMessage,
  PdfFallbackActions,
  PdfFallbackLink,
  PdfFallbackButton,
} from "./PdfPreview.style";

// The worker ships with our bundle: it is always the exact pdfjs-dist version
// react-pdf was built against and does not depend on a third-party CDN being
// reachable from the user's network.
pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

// Transient failures (expired CDN links, flaky networks) get one silent retry
// before the user is asked to act.
const MAX_AUTO_RETRIES = 1;

const hostOf = (url) => {
  try {
    return new URL(url, window.location.href).hostname || "unknown";
  } catch (error) {
    return "invalid";
  }
};

/**
 * Reports a failed PDF load without leaking the file: pdf.js exceptions
 * (`MissingPDFException`, `UnexpectedResponseException`) embed the full URL in
 * their message, so a fresh error carries only the exception name and the
 * tags carry only the hostname. `op` is "load" when pdf.js failed to fetch or
 * parse the file and "source" when react-pdf rejected the `file` descriptor.
 */
export const reportPdfLoadError = (error, url, op = "load") => {
  const errorName = (error && error.name) || "Error";
  const reported = new Error(`PDF preview failed to load (${errorName})`);
  reported.name = "PdfPreviewLoadError";

  const tags = { component: "pdfPreview", op, host: hostOf(url), errorName };
  if (error && Number.isFinite(error.status)) tags.status = error.status;

  Sentry.captureException(reported, { tags });
};

const stopPropagation = (event) => event.stopPropagation();

// Replaces react-pdf's default "Failed to load PDF file." message. The
// controls stop propagation so they never trigger the container's click
// action (which opens the same file in the viewer).
const PdfLoadFallback = ({ url, onRetry }) => {
  const handleRetry = (event) => {
    event.stopPropagation();
    onRetry();
  };

  return (
    <PdfFallback>
      <PdfFallbackMessage>No se pudo mostrar el PDF.</PdfFallbackMessage>
      <PdfFallbackActions>
        <PdfFallbackLink
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={stopPropagation}
        >
          Abrir en una pestaña nueva
        </PdfFallbackLink>
        <PdfFallbackButton type="button" onClick={handleRetry}>
          Reintentar
        </PdfFallbackButton>
      </PdfFallbackActions>
    </PdfFallback>
  );
};

PdfLoadFallback.propTypes = {
  url: PropTypes.string.isRequired,
  onRetry: PropTypes.func.isRequired,
};

const PdfPreviewDocument = ({ url, parentWidth, onFileClick, templateMedia }) => {
  const [fileTotalPages, setFileTotalPages] = useState(null);
  const [displayedPage, setDisplayedPage] = useState(1);
  // Bumping the attempt remounts <Document>, which is the only way to make
  // react-pdf load the same URL again: it only reloads when `file` changes.
  const [loadAttempt, setLoadAttempt] = useState(0);
  const autoRetriesRef = useRef(0);
  // react-pdf 5 reports a rejected `file` (for instance an empty url) through
  // `onSourceError` but never switches to its `error` node, so without this
  // flag the loader would stay visible forever.
  const [sourceFailed, setSourceFailed] = useState(false);

  // react-pdf 5 compares `file` by reference on every update and restarts the
  // download when it changes, so the object must survive re-renders (page
  // navigation, parent updates) or the PDF is fetched again and again.
  const file = useMemo(() => ({ url }), [url]);

  const labels = ["pdfPageCount"];
  const lbls = getLabelMessage(labels);

  const onDocumentLoadSuccess = ({ numPages: pageCount }) => {
    setFileTotalPages(pageCount);
  };

  const retryLoad = () => {
    setSourceFailed(false);
    setLoadAttempt((attempt) => attempt + 1);
  };

  // Reports the failure and spends the retry budget; returns whether an
  // automatic retry was started.
  const handleLoadFailure = (error, op) => {
    reportPdfLoadError(error, url, op);
    if (autoRetriesRef.current >= MAX_AUTO_RETRIES) return false;
    autoRetriesRef.current += 1;
    retryLoad();
    return true;
  };

  const onDocumentLoadError = (error) => {
    // react-pdf renders the `error` node on its own for this path.
    handleLoadFailure(error, "load");
  };

  const onDocumentSourceError = (error) => {
    if (!handleLoadFailure(error, "source")) setSourceFailed(true);
  };

  const previousPdfPage = () => {
    if (displayedPage > 1)
      setDisplayedPage((actualPageNumber) => actualPageNumber - 1);
  };

  const nextPdfPage = () => {
    if (displayedPage < fileTotalPages)
      setDisplayedPage((actualPageNumber) => actualPageNumber + 1);
  };

  const containerRef = useRef(null);

  return (
    <PdfContainer ref={containerRef}>
      <DocumentContainer onClick={onFileClick} hasClickAction={!!onFileClick}>
        {sourceFailed ? (
          <PdfLoadFallback url={url} onRetry={retryLoad} />
        ) : (
          <Document
            key={loadAttempt}
            className="pdfDocument"
            file={file}
            loading={<LoaderComponent width={100} height={100} />}
            error={<PdfLoadFallback url={url} onRetry={retryLoad} />}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            onSourceError={onDocumentSourceError}
          >
            {parentWidth && (
              <Page
                pageNumber={displayedPage}
                renderAnnotationLayer={false}
                className="pdfDocument"
              />
            )}
            {!parentWidth && (
              <Page
                pageNumber={displayedPage}
                renderAnnotationLayer={false}
                className="pdfDocument"
              />
            )}
          </Document>
        )}
      </DocumentContainer>
      {!templateMedia && (
        <PdfPageCounterContainer>
          <PreviousPdfButton onClick={previousPdfPage}>
            <PreviousPdfPage icon={faChevronLeft} />
          </PreviousPdfButton>
          {fileTotalPages && (
            <PageCounter>
              {lbls.pdfPageCount(displayedPage, fileTotalPages)}
            </PageCounter>
          )}
          <NextPdfButton onClick={nextPdfPage}>
            <NextPdfPage icon={faChevronRight} />
          </NextPdfButton>
        </PdfPageCounterContainer>
      )}
    </PdfContainer>
  );
};

PdfPreviewDocument.propTypes = {
  url: PropTypes.string.isRequired,
  parentWidth: PropTypes.number,
  onFileClick: PropTypes.func,
  templateMedia: PropTypes.bool,
};

// A new URL is a new document: keying by it resets the displayed page, the
// page count and the retry budget instead of carrying them over.
const PdfPreview = (props) => <PdfPreviewDocument key={props.url} {...props} />;

PdfPreview.propTypes = {
  url: PropTypes.string.isRequired,
  parentWidth: PropTypes.number,
  onFileClick: PropTypes.func,
  templateMedia: PropTypes.bool,
};

export default PdfPreview;
